import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import {
  GENDEC_LOOKAHEAD_DEFAULT,
  GENDEC_LOOKAHEAD_MAX,
  GENDEC_LOOKAHEAD_MIN,
  IFlightType,
  IGendecStationConfig,
  IGendecStationConfigErrors,
  firstGendecStationConfigError,
  isValidEmail,
  validateStationConfig,
  validateStationConfigs,
} from "@vmosolutions/oc-interfaces";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IAirportOption } from "src/redux/reducers/StructuresReducer";
import { updateAirportOptions } from "src/utils/airportHelper";
import { EmailChipField } from "./EmailChipField";

interface IStationConfigsEditorProps {
  configs: IGendecStationConfig[];
  onChange: (next: IGendecStationConfig[]) => void;
  flightTypes: IFlightType[];
  /**
   * When set, the editor only shows entries for this airport's `Ident` (or `IATACode`)
   * and hides the airport-search bar - meant for embedding inside the Airport admin
   * tab. When omitted, the editor shows all configured airports as accordions plus a
   * search bar to add a new airport.
   */
  airportFilter?: string;
}

interface IConfigRowProps {
  config: IGendecStationConfig;
  /** True when this entry has no `flightTypeId` (the airport's Default row). */
  isDefault: boolean;
  flightTypes: IFlightType[];
  onChange: (patch: Partial<IGendecStationConfig>) => void;
  onDelete: () => void;
  /** Other flight-type ids configured for this airport, to disable in the picker. */
  takenFlightTypeIds: string[];
  /**
   * Whether this row should start expanded. Parent computes this from "freshly added
   * this session" (so a just-clicked airport opens its Default for editing); rows that
   * existed at load time start collapsed. ConfigRow OR's this with its own error check
   * so an override with a missing flight type still auto-opens.
   */
  defaultExpanded?: boolean;
}

/**
 * One half-row form field: `<Grid item md={6}>` + `<TextField fullWidth size="small" ...>`.
 * Used by Reply To and Look-ahead, where the only thing that varies is the label and
 * input type. Pulling this out collapses two near-identical 10-line blocks per row.
 */
interface IRowFieldProps {
  label: string;
  value: string | number;
  onChange: (raw: string) => void;
  error?: string;
  hint: string;
  type?: "text" | "number";
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

const RowField = (props: IRowFieldProps): JSX.Element => (
  <Grid item xs={12} md={6}>
    <TextField
      fullWidth
      size="small"
      type={props.type ?? "text"}
      label={props.label}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      error={!!props.error}
      helperText={props.error ?? props.hint}
      inputProps={props.inputProps}
    />
  </Grid>
);

/** AccordionSummary contents for a ConfigRow - title chip + recipient count + delete. */
const ConfigRowHeader = (props: {
  title: string;
  titleColor: "error" | "text.secondary";
  recipientCount: number;
  hasAnyError: boolean;
  showDelete: boolean;
  onDelete: () => void;
}): JSX.Element => {
  const { title, titleColor, recipientCount, hasAnyError, showDelete, onDelete } = props;
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, mr: 1 }}>
      <Typography variant="subtitle2" color={titleColor}>
        {title}
      </Typography>
      <Chip
        size="small"
        label={`${recipientCount} recipient${recipientCount === 1 ? "" : "s"}`}
        color={recipientCount === 0 ? "default" : "primary"}
        variant={recipientCount === 0 ? "outlined" : "filled"}
      />
      {hasAnyError && <Chip size="small" color="error" label="Invalid" />}
      <Box sx={{ flex: 1 }} />
      {showDelete && (
        <Tooltip title="Remove this flight-type override">
          <IconButton
            size="small"
            onClick={(e) => {
              // Stop the accordion from toggling when the user clicks delete.
              e.stopPropagation();
              onDelete();
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

/**
 * Render a single station-config entry as a collapsible accordion-style card. The
 * Default row (first per airport) defaults to expanded; override rows default to
 * collapsed and auto-expand when invalid.
 */
const ConfigRow = (props: IConfigRowProps): JSX.Element => {
  const { config, isDefault, flightTypes, onChange, onDelete, takenFlightTypeIds, defaultExpanded } = props;
  const theme = useTheme();
  const errors: IGendecStationConfigErrors = validateStationConfig(config);
  const errMsg = firstGendecStationConfigError(errors);
  const flightTypeMissing = !isDefault && !config.flightTypeId;
  const hasAnyError = !!errMsg || flightTypeMissing;
  const flightTypeName = useMemo(() => {
    if (isDefault) return "Default";
    if (!config.flightTypeId) return "(select a flight type)";
    return flightTypes.find((t) => String(t._id) === config.flightTypeId)?.title ?? config.flightTypeId;
  }, [isDefault, config.flightTypeId, flightTypes]);
  // Controlled expansion. Initial value combines the parent's hint (`defaultExpanded`,
  // true only for freshly-added Defaults) with `hasAnyError` so a newly-spawned override
  // with a missing flight type still auto-opens. After mount the user fully controls it.
  const [expanded, setExpanded] = useState<boolean>(!!defaultExpanded || hasAnyError);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, next) => setExpanded(next)}
      disableGutters
      square
      elevation={0}
      sx={{
        borderLeft: 3,
        borderColor: isDefault ? "primary.main" : flightTypeMissing ? "error.main" : "secondary.main",
        backgroundColor: theme.palette.action.hover,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <ConfigRowHeader
          title={flightTypeName}
          titleColor={flightTypeMissing ? "error" : "text.secondary"}
          recipientCount={(config.sendTo ?? []).length}
          hasAnyError={hasAnyError}
          showDelete={!isDefault}
          onDelete={onDelete}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 1, pb: 1 }}>
        {!isDefault && (
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Flight Type"
                value={config.flightTypeId ?? ""}
                onChange={(e) => onChange({ flightTypeId: e.target.value })}
                error={!!errors.flightTypeId || flightTypeMissing}
                helperText={errors.flightTypeId ?? (flightTypeMissing ? "Select a flight type" : " ")}
              >
                {flightTypes.map((ft) => {
                  const id = String(ft._id);
                  const taken = id !== config.flightTypeId && takenFlightTypeIds.includes(id);
                  return (
                    <MenuItem key={id} value={id} disabled={taken}>
                      {ft.title}
                      {taken ? " (already configured)" : ""}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
          </Grid>
        )}

        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <EmailChipField
              label="Send To"
              value={config.sendTo ?? []}
              onChange={(next) => onChange({ sendTo: next })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <EmailChipField
              // Wording calls out the additive semantics so the operator doesn't expect
              // this list to replace the global cc. Backend resolver unions global +
              // station, deduped case-insensitively (see resolveStationRecipients).
              label="Additional CC (added to global CC)"
              value={config.cc ?? []}
              onChange={(next) => onChange({ cc: next })}
            />
          </Grid>
          <RowField
            label="Reply To (optional)"
            value={config.replyTo ?? ""}
            onChange={(raw) => onChange({ replyTo: raw })}
            error={errors.replyTo}
            hint="Leave blank to inherit the global Reply To"
          />
          <RowField
            label={`Look-ahead (h)${isDefault ? "" : " - override"}`}
            type="number"
            value={config.lookAheadHours ?? ""}
            onChange={(raw) => onChange({ lookAheadHours: raw === "" ? undefined : Number(raw) })}
            error={errors.lookAheadHours}
            hint={
              isDefault
                ? `${GENDEC_LOOKAHEAD_MIN}-${GENDEC_LOOKAHEAD_MAX}, default ${GENDEC_LOOKAHEAD_DEFAULT}`
                : "Override this airport's default for this flight type only"
            }
            inputProps={{ min: GENDEC_LOOKAHEAD_MIN, max: GENDEC_LOOKAHEAD_MAX, step: 1 }}
          />
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

interface IAirportPanelProps {
  ident: string;
  /** IATA code shown next to the ICAO in the header when known. */
  iata?: string;
  slice: IGendecStationConfig[];
  /** Just-added-this-session airports open by default so the user can start editing. */
  freshlyCreated: boolean;
  /** Operator clicked the trash icon on this airport's header - parent confirms + removes. */
  onRequestDelete: () => void;
  children: React.ReactNode;
}

/**
 * Per-airport accordion in the GenDec-tab variant. Owns its own `expanded` state so
 * the controlled prop is stable (avoids MUI's "default state changed after init"
 * warning when validation flips between renders). Summary text is muted when every
 * entry under this airport is valid - that way the airport ident only draws the eye
 * when something needs attention; when invalid we recolor the text to error red.
 */
const AirportPanel = (props: IAirportPanelProps): JSX.Element => {
  const { ident, iata, slice, freshlyCreated, onRequestDelete, children } = props;
  const ftCount = slice.filter((c) => c.flightTypeId != null && c.flightTypeId !== "").length;
  // Per-entry + cross-entry validation in one call - the slice is already per-airport,
  // so the cross-entry invariants (one Default per airport, no duplicate flight-type
  // pairs) operate entirely within it.
  const invalidCount = validateStationConfigs(slice).filter((e) => e !== undefined).length;
  const hasInvalid = invalidCount > 0;
  // Initialize controlled state once: open if freshly added (so the new airport is ready
  // to edit) or if anything is invalid on load. After mount the user fully controls it.
  const [expanded, setExpanded] = useState<boolean>(freshlyCreated || hasInvalid);
  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, next) => setExpanded(next)}
      disableGutters
      sx={{ "&:before": { display: "none" } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, mr: 1 }}>
          <Typography variant="subtitle1" color={hasInvalid ? "error" : "text.primary"}>
            {ident}
            {iata ? ` (${iata})` : ""}
          </Typography>
          {ftCount > 0 && <Chip size="small" label={`${ftCount} flight-type override${ftCount === 1 ? "" : "s"}`} />}
          {hasInvalid && <Chip size="small" color="error" label={`${invalidCount} invalid`} />}
          <Box sx={{ flex: 1 }} />
          <Tooltip title={`Remove ${ident}'s station configuration`}>
            <IconButton
              size="small"
              onClick={(e) => {
                // Stop the accordion from toggling when the user clicks delete.
                e.stopPropagation();
                onRequestDelete();
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
};

/**
 * Reusable per-(airport, optional flight type) recipient editor. Used in two places:
 *   1. GenDec Management tab - all configured airports as accordions + an airport
 *      search bar to add a new one.
 *   2. Airport admin tab - scoped to one airport via `airportFilter`; renders the
 *      airport's Default row and per-flight-type rows inline (no accordion, no search).
 *
 * The component is uncontrolled regarding dirty state - the parent reads `configs` /
 * receives `onChange` and tracks dirtiness itself.
 */
export const StationConfigsEditor = (props: IStationConfigsEditorProps): JSX.Element => {
  const { configs, onChange, flightTypes, airportFilter } = props;

  // Track which airports were added in THIS editor session. Used to differentiate
  // "operator just clicked Add Airport" (open the Default for editing) from "this
  // airport was loaded from the server" (start everything collapsed). The set persists
  // across renders but doesn't survive a remount of the editor itself, which matches
  // the intent: revisiting the tab counts as a fresh session.
  const [freshAirports, setFreshAirports] = useState<Set<string>>(new Set());
  // Pending airport-removal confirmation. Holds the ident the operator clicked "remove"
  // on; null means no dialog is open. The actual removal goes through onChange (so the
  // parent's Reset button can undo before save).
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const dispatch = useDispatch();
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);
  const allAirports = useMemo(() => Object.values(airportOptions), [airportOptions]);

  // Keep the latest airportOptions in a ref so the debounced handler stays stable
  // The handler is created once and reads the ref at call time.
  const airportOptionsRef = useRef(airportOptions);
  airportOptionsRef.current = airportOptions;
  const handleAirportSearchInput = useMemo(
    () =>
      debounce((input: string) => {
        if (input && input.length >= 3) {
          void updateAirportOptions(input, airportOptionsRef.current, dispatch);
        }
      }, 400),
    [dispatch]
  );
  // Cancel any pending debounced fetch on unmount so it can't dispatch into a torn-down tree.
  useEffect(() => () => handleAirportSearchInput.cancel(), [handleAirportSearchInput]);

  /**
   * IATA-by-ICAO lookup so the airport summary can render "MMMY (MTY) ..." instead of
   * just "MMMY". `airportOptions` may not contain every airport an operator has a
   * stationConfig for (the user might have configured airports they've never searched
   * this session); the effect below triggers a fetch for any missing one so the IATA
   * fills in shortly after mount.
   */
  const iataByIdent = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of allAirports) {
      if (a.ident && a.iata) m.set(a.ident.toUpperCase(), a.iata);
    }
    return m;
  }, [allAirports]);

  // Group configs by airport ident (uppercased for case-insensitive matching). When
  // airportFilter is set, we still group so we can pull just that airport's slice.
  const byIdent = useMemo(() => {
    const map = new Map<string, IGendecStationConfig[]>();
    for (const c of configs) {
      const key = (c.airportIdent ?? "").toUpperCase();
      const arr = map.get(key) ?? [];
      arr.push(c);
      map.set(key, arr);
    }
    return map;
  }, [configs]);

  // Eagerly pull airport details for any configured airport that's not yet in the
  // redux cache, so its IATA can appear in the summary.
  const attemptedIdents = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const ident of byIdent.keys()) {
      if (ident.length >= 3 && !airportOptions[ident] && !attemptedIdents.current.has(ident)) {
        attemptedIdents.current.add(ident);
        void updateAirportOptions(ident, airportOptions, dispatch);
      }
    }
  }, [byIdent, airportOptions, dispatch]);

  // Free-text filter applied in the full GenDec-tab variant (no effect in the
  // airport-tab embed, which is already scoped via `airportFilter`).
  const [filterText, setFilterText] = useState<string>("");
  const filteredAirports = useMemo(() => {
    if (airportFilter) return [airportFilter.toUpperCase()];
    const all = Array.from(byIdent.keys()).sort();
    const needle = filterText.trim().toUpperCase();
    if (!needle) return all;
    return all.filter((ident) => {
      if (ident.includes(needle)) return true;
      const iata = iataByIdent.get(ident);
      return !!iata && iata.toUpperCase().includes(needle);
    });
  }, [byIdent, airportFilter, filterText, iataByIdent]);

  const replaceConfig = useCallback(
    (oldConfig: IGendecStationConfig, patch: Partial<IGendecStationConfig>) => {
      onChange(configs.map((c) => (c === oldConfig ? { ...c, ...patch } : c)));
    },
    [configs, onChange]
  );

  const removeConfig = useCallback(
    (oldConfig: IGendecStationConfig) => {
      onChange(configs.filter((c) => c !== oldConfig));
    },
    [configs, onChange]
  );

  /**
   * Remove every config (Default + all overrides) for a given airport. Goes through
   * onChange so the parent's dirty flag fires and Reset undoes it before save. Also
   * clears the freshlyCreated flag so re-adding the same airport later starts fresh.
   */
  const removeAirport = useCallback(
    (ident: string) => {
      const u = ident.toUpperCase();
      onChange(configs.filter((c) => (c.airportIdent ?? "").toUpperCase() !== u));
      setFreshAirports((prev) => {
        if (!prev.has(u)) return prev;
        const next = new Set(prev);
        next.delete(u);
        return next;
      });
    },
    [configs, onChange]
  );

  /** Append a new Default-entry config for an airport that has no entries yet. */
  const addAirport = useCallback(
    (airport: IAirportOption | null) => {
      if (!airport) return;
      const ident = (airport.ident ?? "").toUpperCase();
      if (!ident) return;
      if (byIdent.has(ident)) return; // already configured
      onChange([...configs, { airportIdent: ident, sendTo: [], cc: [] }]);
      // Mark as freshly added so the AirportPanel + Default ConfigRow open by default.
      setFreshAirports((prev) => {
        const next = new Set(prev);
        next.add(ident);
        return next;
      });
    },
    [configs, byIdent, onChange]
  );

  /**
   * Append a new flight-type-specific entry under an airport. The first entry per
   * airport is the Default; every subsequent entry is treated as an override and must
   * have `flightTypeId` set before save (the validator + the picker's error state
   * enforce this). We deliberately create the override with `flightTypeId: undefined`
   * so the picker spawns in its "unselected" state - the user has to make a choice.
   */
  const addFlightTypeEntry = useCallback(
    (ident: string) => {
      onChange([...configs, { airportIdent: ident, flightTypeId: undefined, sendTo: [], cc: [] }]);
    },
    [configs, onChange]
  );

  const renderAirportSlice = (ident: string): JSX.Element => {
    const slice = byIdent.get(ident) ?? [];
    // Insertion order is meaningful: the first entry per airport is the Default; every
    // entry after it is an override. We don't sort so a user can't accidentally re-order
    // Default-vs-override semantics by editing fields.
    const takenFlightTypeIds = slice
      .map((c, i) => (i === 0 ? null : c.flightTypeId))
      .filter((id): id is string => !!id);
    return (
      <>
        {/* CSS Grid: cards flow side-by-side when the viewport is wide enough to fit
            two at >=480px each, and stack vertically when it isn't. `alignItems: start`
            stops every card in a row from inheriting the tallest card's height. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(480px, 1fr))",
            alignItems: "start",
            gap: 1,
            mb: 1,
          }}
        >
          {slice.map((c, i) => (
            <ConfigRow
              // Positional key on purpose: a content-based key (e.g. flightTypeId) would
              // change the moment the user picks a flight type, causing React to unmount
              // and remount the component - which re-runs the useState initializer and
              // collapses the accordion mid-edit. The cost is a rare expand-state leak
              // when an override is deleted from the middle of the list (the surviving
              // entries shift index), which is much less disruptive than collapse-on-pick.
              key={`${ident}-${i}`}
              config={c}
              isDefault={i === 0}
              flightTypes={flightTypes}
              takenFlightTypeIds={takenFlightTypeIds}
              // Only the freshly-added airport's Default row auto-opens; loaded-from-server
              // Defaults stay collapsed when the user expands their airport accordion.
              defaultExpanded={i === 0 && freshAirports.has(ident)}
              onChange={(patch) => replaceConfig(c, patch)}
              onDelete={() => removeConfig(c)}
            />
          ))}
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => addFlightTypeEntry(ident)}
          disabled={slice.length === 0 || takenFlightTypeIds.length >= flightTypes.length}
        >
          Add flight-type override
        </Button>
      </>
    );
  };

  // Compact airport-tab variant: no accordions, no search bar; just render this one
  // airport's slice. If no config exists yet for this airport, show a one-click button
  // to bootstrap the Default entry - otherwise the operator has no way to start (the
  // global "Add Airport" search bar is hidden in this variant).
  if (airportFilter) {
    const filterIdent = airportFilter.toUpperCase();
    const slice = byIdent.get(filterIdent) ?? [];
    if (slice.length === 0) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No GenDec recipients configured for this airport. The global Send To and CC apply until you set one up.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => onChange([...configs, { airportIdent: filterIdent, sendTo: [], cc: [] }])}
          >
            Set up GenDec recipients for {filterIdent}
          </Button>
        </Box>
      );
    }
    return <Box>{renderAirportSlice(filterIdent)}</Box>;
  }

  // Full GenDec-tab variant: airport-search header + an accordion per configured airport.
  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Autocomplete
          sx={{ minWidth: 320 }}
          options={allAirports}
          getOptionLabel={(option: IAirportOption) =>
            `${option.ident}${option.iata ? ` (${option.iata})` : ""} - ${option.name}`
          }
          isOptionEqualToValue={(a, b) => a.ident === b.ident}
          onInputChange={(_e, value) => handleAirportSearchInput(value)}
          onChange={(_e, value) => addAirport(value)}
          renderInput={(params) => (
            <TextField {...params} size="small" label="Add Airport" placeholder="Search ICAO / IATA / name" />
          )}
        />
        {/* Local filter over already-configured airports. Doesn't affect the underlying
            data; just narrows what the accordion list renders so the operator can pin
            down a specific airport in a long list. */}
        <TextField
          size="small"
          label="Filter"
          placeholder="ICAO or IATA"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          sx={{ minWidth: 200 }}
        />
      </Stack>

      {filteredAirports.length === 0 && !filterText && (
        <Typography variant="body2" color="text.secondary">
          No per-airport configurations yet. The global Send To / CC apply until you add one.
        </Typography>
      )}
      {filteredAirports.length === 0 && filterText && (
        <Typography variant="body2" color="text.secondary">
          No configured airport matches &quot;{filterText}&quot;.
        </Typography>
      )}

      {filteredAirports.map((ident) => (
        <AirportPanel
          key={ident}
          ident={ident}
          iata={iataByIdent.get(ident)}
          slice={byIdent.get(ident) ?? []}
          freshlyCreated={freshAirports.has(ident)}
          onRequestDelete={() => setPendingDelete(ident)}
        >
          {renderAirportSlice(ident)}
        </AirportPanel>
      ))}

      <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove station configuration?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove all GenDec station configurations for <strong>{pendingDelete}</strong>? This counts as a change -
            click <strong>Reset</strong> to undo, or <strong>Update</strong> at the top of the tab to save.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (pendingDelete) removeAirport(pendingDelete);
              setPendingDelete(null);
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/** Lightweight email-validity helper exported for the parent if it wants its own check. */
export const stationConfigHasEmailIssues = (configs: IGendecStationConfig[]): boolean =>
  configs.some((c) => (c.sendTo ?? []).some((e) => !isValidEmail(e)) || (c.cc ?? []).some((e) => !isValidEmail(e)));

export default StationConfigsEditor;
